import { useEffect, useRef } from "react";

const PLAYED_COLOR = "#642CFF";
const UNPLAYED_COLOR = "#C1AFFF";
const BAR_WIDTH = 3;
const BAR_GAP = 2;
const BAR_RADIUS = 2;
const MIN_BAR_HEIGHT = 4;
const POWER_CURVE = 0.6; // < 1 lifts quiet bars, keeping dynamic contrast
const SILENCE_THRESHOLD = 0.05; // below this peak avg, audio is treated as near-silent
const HEIGHT = 27;

interface PlaybackWaveformProps {
  audioData: Float32Array;
  getPlayedRatio: () => number;
}

export function PlaybackWaveform({
  audioData,
  getPlayedRatio,
}: PlaybackWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeObserver = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.scale(dpr, dpr);
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let rafId: number;

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);

      if (!audioData || audioData.length === 0) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      const step = BAR_WIDTH + BAR_GAP;
      const barCount = Math.floor(rect.width / step);

      if (barCount <= 0) {
        rafId = requestAnimationFrame(draw);
        return;
      }

      const samples = Math.max(1, Math.floor(audioData.length / barCount));
      const ratio = getPlayedRatio() ?? 0;

      // Pass 1: compute average amplitude per bar and track the peak
      const barAverages: number[] = [];
      let peakAvg = 0;

      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (
          let j = 0;
          j < samples && i * samples + j < audioData.length;
          j++
        ) {
          sum += Math.abs(audioData[i * samples + j]);
        }
        const avg = sum / samples;
        barAverages.push(avg);
        if (avg > peakAvg) peakAvg = avg;
      }

      // Pass 2: normalize to peak (clamped to silence threshold) so quiet
      // recordings don't get inflated to full height
      const effectivePeak = Math.max(peakAvg, SILENCE_THRESHOLD);
      for (let i = 0; i < barCount; i++) {
        const normalized = barAverages[i] / effectivePeak;
        const curved = normalized ** POWER_CURVE;
        const h = Math.max(MIN_BAR_HEIGHT, curved * rect.height);
        const x = i * step;
        const y = rect.height / 2 - h / 2;

        ctx.fillStyle = i / barCount < ratio ? PLAYED_COLOR : UNPLAYED_COLOR;

        if (BAR_RADIUS > 0) {
          ctx.beginPath();
          ctx.roundRect(x, y, BAR_WIDTH, h, BAR_RADIUS);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, BAR_WIDTH, h);
        }
      }

      rafId = requestAnimationFrame(draw);
    };

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [audioData, getPlayedRatio]);

  return (
    <div
      className="relative lg:w-54 w-47.5"
      ref={containerRef}
      style={{ height: HEIGHT }}
      role="img"
      aria-label="Audio waveform"
    >
      <canvas className="block h-full w-full" ref={canvasRef} />
    </div>
  );
}
